import { DashboardLegendType } from "@app/dashboard/shared/enums/DashboardLegendType.enum";
import { KeyValue } from "@models/generic/KeyValuePair";
import { Component, Input, OnChanges, inject } from "@angular/core";
import { provideTranslocoScope, TranslocoService } from "@jsverse/transloco";
import {
    DashboardGraphType,
    DashboardGraphTypeDescription,
} from "../shared/enums/DashboardGraphType.enum";
import { DashboardWidgetModel } from "../shared/models/DashboardWidgetModel";
import { DashboardLegendTypeDescription } from "../shared/enums/DashboardLegendType.enum";
import { GenericListField } from "@app/shared/models/GenericList/GenericListField";
import { WidgetConfigType } from "../shared/enums/WidgetConfigType.enum";
import { translateWidgetCatalogField } from "../shared/translateWidgetCatalogField";

@Component({
    selector: "widget-configuration",
    templateUrl: "./widget-configuration.component.html",
    styleUrls: ["./widget-configuration.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope("dashboard")],
})
export class WidgetConfigurationComponent implements OnChanges {
    private _transloco = inject(TranslocoService);

    @Input() config: DashboardWidgetModel;

    additionalData = {};

    widgetSettings: GenericListField[] = [];
    dataFilters: GenericListField[] = [];

    graphItems: KeyValue<DashboardGraphType, string>[] = [];
    legendItems: KeyValue<DashboardLegendType, string>[] = [];

    globalFilterFieldNames = "";

    constructor() {}

    ngOnChanges(): void {
        this.reset();
        this.setDefaults();
    }

    private reset() {
        this.widgetSettings = [];
        this.dataFilters = [];
        this.graphItems = [];
        this.legendItems = [];
    }

    private setDefaults() {
        if (this.config.customConfig.customTitle === this.config.title) {
            this.config.customConfig.customTitle = "";
        }

        if (!this.config.customConfig.customIcon) {
            this.config.customConfig.customIcon = this.config.icon;
        }

        this.setConfigurations();
        this.setLists();
    }

    filterChanged() {
        if (this.config.api.onFilterChanged) {
            this.config.api.onFilterChanged();
        }
    }

    private setConfigurations() {
        // DashboardWidgetMapping.get() keeps `configurations` by reference (not a deep copy), so
        // clone each field before we mutate it below -- otherwise translateField() would permanently
        // rewrite the shared static catalog for the rest of the app session.
        const clonedConfigurations = this.config.configurations.map((wc) => ({ ...wc }));

        this.widgetSettings = clonedConfigurations.filter(
            (wc) => wc.configType === WidgetConfigType.WidgetConfig
        );

        this.dataFilters = clonedConfigurations.filter(
            (wc) => wc.configType === WidgetConfigType.DataFilter
        );

        // widgetSettings/dataFilters render through the shared generic-field-editor, which has
        // no transloco pipe of its own -- DashboardWidgetMapping's prettyName/description/dropdown
        // Name values are "dashboard.widgetCatalog.*" keys, so resolve them here first.
        [...this.widgetSettings, ...this.dataFilters].forEach((field) =>
            translateWidgetCatalogField(this._transloco, field)
        );

        if (this.config.globalFilterKeys && this.config.globalFilterKeys.length > 0) {
            const filtersForGlobal = this.config.globalFilterKeys
                .map((k) => this.dataFilters.find((d) => d.fieldName === k))
                .filter((k) => k)
                .map((k) => k.prettyName);

            this.globalFilterFieldNames = filtersForGlobal.join("\n");
        } else {
            this.globalFilterFieldNames = "";
        }
    }

    private setLists() {
        this.graphItems = this.getList(
            this.config.allowedChartTypes,
            DashboardGraphTypeDescription
        );

        if (this.config.legendConfigurable) {
            this.legendItems = this.getList(
                Object.keys(DashboardLegendTypeDescription).map((k) => <DashboardLegendType>+k),
                DashboardLegendTypeDescription
            );
        } else {
            this.legendItems = [];
        }
    }

    private getList<T>(configItems: T[], descriptions: any): KeyValue<T, string>[] {
        if (!configItems) {
            return [];
        }

        return configItems.map(
            (ci) => new KeyValue<T, string>(ci, this._transloco.translate(descriptions[ci]))
        );
    }
}
